import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Button, Card, Col, Flex, Form, Row, Typography } from 'antd'
import { BookOpen, Check } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ChecklistItem } from 'src/analytics'
import {
  type OrganizationAuthMethod,
  type OrganizationAuthMethodsResponse,
  type OrganizationAuthMethodUpdate,
  type OrganizationCustomAuthProvider,
  organizationsOrganizationsAuthMethodsPartialUpdate,
  organizationsOrganizationsAuthMethodsRetrieveOptions,
  organizationsOrganizationsAuthMethodsRetrieveQueryKey,
  organizationsOrganizationsCustomAuthProvidersListOptions,
  organizationsOrganizationsRetrieveQueryKey
} from 'src/api/main'
import { ContentSpinner, FormActionsBar, QueryErrorAlert } from 'src/components'
import { DOCS_LINKS } from 'src/constants'
import { buildAuthMethodsPayload } from 'src/features/organizations/components/OrganizationSettings/authMethodsPayload'
import { useReportJourneyStep } from 'src/features/workspaces/onboarding'
import { useInvalidatePredicateQueries } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

import { AdditionalSettingsCard } from './AdditionalSettingsCard'
import { BuiltInProviderRow } from './BuiltInProviderRow'
import { BUILT_IN_PROVIDERS } from './constants'
import { useStyles } from './styles'
import type { AuthMethodFormValues } from './types'
import { selectCustomProviders } from './utils'

const { Text } = Typography

/**
 * The generated key, not a hand-made one: the getting-started checklist reads the
 * same endpoint to decide whether authentication has been set up, so saving here
 * has to invalidate its copy too. A private key would leave the checklist showing
 * a stale "not done" for as long as its cache lived.
 */
const authMethodsQueryArg = (organizationId: number) => ({
  path: { id: organizationId }
})

type Props = {
  workspaceId: number
  idValid: boolean
}

export const SignInMethodsTab = ({ workspaceId, idValid }: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { message } = App.useApp()
  const reportStep = useReportJourneyStep(workspaceId)
  const queryClient = useQueryClient()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const showApiErrors = useShowApiErrors()
  const [form] = Form.useForm()

  const {
    data: authMethodsResponse,
    isLoading: methodsLoading,
    isError: methodsError,
    refetch: refetchMethods
  } = useQuery({
    ...organizationsOrganizationsAuthMethodsRetrieveOptions(
      authMethodsQueryArg(workspaceId)
    ),
    enabled: idValid
  })

  const { data: customProviders = [] } = useQuery({
    ...organizationsOrganizationsCustomAuthProvidersListOptions({
      path: { id: workspaceId }
    }),
    enabled: idValid,
    select: selectCustomProviders
  })

  const authMethodsList = useMemo<OrganizationAuthMethod[]>(() => {
    if (Array.isArray(authMethodsResponse)) {
      return authMethodsResponse
    }
    if (authMethodsResponse && 'auth_methods' in authMethodsResponse) {
      return authMethodsResponse.auth_methods ?? []
    }
    return []
  }, [authMethodsResponse])

  const additionalSettings = useMemo(() => {
    const envelope = authMethodsResponse as
      | OrganizationAuthMethodsResponse
      | undefined

    return {
      enforce_sso: envelope?.enforce_sso ?? false,
      allow_self_service_join: envelope?.allow_self_service_join ?? false,
      auto_provision_users: envelope?.auto_provision_users ?? false
    }
  }, [authMethodsResponse])

  useEffect(() => {
    const values: Record<string, unknown> = {}
    BUILT_IN_PROVIDERS.forEach((descriptor) => {
      const saved = authMethodsList.find((m) => m.method === descriptor.method)
      values[`${descriptor.key}_enabled`] = saved?.enabled ?? false
      values[`${descriptor.key}_client_id`] = saved?.client_id ?? ''
      values[`${descriptor.key}_client_secret`] = ''
      values[`${descriptor.key}_tenant_id`] = saved?.tenant_id ?? ''
      values[`${descriptor.key}_key_id`] = saved?.key_id ?? ''
    })
    values.enforce_sso = additionalSettings.enforce_sso
    values.allow_self_service_join = additionalSettings.allow_self_service_join
    values.auto_provision_users = additionalSettings.auto_provision_users
    form.setFieldsValue(values)
  }, [authMethodsList, additionalSettings, form])

  const { mutate: patchAuthMethods, isPending: saving } = useMutation({
    mutationFn: (body: {
      auth_methods: OrganizationAuthMethodUpdate[]
      enforce_sso: boolean
      allow_self_service_join: boolean
      auto_provision_users: boolean
    }) =>
      organizationsOrganizationsAuthMethodsPartialUpdate({
        path: { id: workspaceId },
        body
      }),
    onSuccess: () => {
      reportStep(ChecklistItem.AUTHENTICATION)
      message.success(t('information_updated_success'))
      invalidateQueries([
        organizationsOrganizationsRetrieveQueryKey({
          path: { id: workspaceId }
        })
      ])
      queryClient.invalidateQueries({
        queryKey: organizationsOrganizationsAuthMethodsRetrieveQueryKey(
          authMethodsQueryArg(workspaceId)
        )
      })
    },
    onError: (error) => showApiErrors(error, form)
  })

  const handleSubmit = (values: AuthMethodFormValues) => {
    const payload = buildAuthMethodsPayload(values as Record<string, unknown>)
    patchAuthMethods({
      auth_methods: payload as OrganizationAuthMethodUpdate[],
      enforce_sso: Boolean(values.enforce_sso),
      allow_self_service_join: Boolean(values.allow_self_service_join),
      auto_provision_users: Boolean(values.auto_provision_users)
    })
  }

  const handleReset = () => {
    const values: Record<string, unknown> = {}
    BUILT_IN_PROVIDERS.forEach((descriptor) => {
      const saved = authMethodsList.find((m) => m.method === descriptor.method)
      values[`${descriptor.key}_enabled`] = saved?.enabled ?? false
      values[`${descriptor.key}_client_id`] = saved?.client_id ?? ''
      values[`${descriptor.key}_client_secret`] = ''
      values[`${descriptor.key}_tenant_id`] = saved?.tenant_id ?? ''
      values[`${descriptor.key}_key_id`] = saved?.key_id ?? ''
    })
    values.enforce_sso = additionalSettings.enforce_sso
    values.allow_self_service_join = additionalSettings.allow_self_service_join
    values.auto_provision_users = additionalSettings.auto_provision_users
    form.setFieldsValue(values)
  }

  const enabledCount = authMethodsList.filter((m) => m.enabled).length
  const customEnabledCount = customProviders.filter(
    (p: OrganizationCustomAuthProvider) => p.enabled
  ).length

  if (!idValid) {
    return null
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => handleSubmit(values as AuthMethodFormValues)}
      requiredMark={false}
    >
      <Flex vertical gap={14}>
        {methodsError ? (
          <QueryErrorAlert onRetry={() => void refetchMethods()} />
        ) : null}

        {methodsLoading && !authMethodsResponse ? (
          <ContentSpinner minHeight={160} />
        ) : (
          <>
            <Row gutter={14}>
              <Col xs={24} lg={14}>
                <Card
                  size="small"
                  className={styles.providersCard}
                  styles={{ body: { padding: 0 } }}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    className={styles.providersHeader}
                  >
                    <Flex vertical style={{ lineHeight: 1.2 }}>
                      <Flex align="center" gap={10} wrap="wrap">
                        <Text strong style={{ fontSize: 14 }}>
                          {t('workspace.authentication.providersCard.title')}
                        </Text>
                        <Flex align="center" gap={10} wrap="wrap">
                          <span className={styles.statInline}>
                            <span className={styles.statInlineLabel}>
                              {t('workspace.authentication.summary.active')}
                            </span>
                            <span className={styles.statInlineValue}>
                              {enabledCount}
                            </span>
                          </span>
                          <span className={styles.statInline}>
                            <span className={styles.statInlineLabel}>
                              {t('workspace.authentication.summary.custom')}
                            </span>
                            <span className={styles.statInlineValue}>
                              {customEnabledCount}
                            </span>
                          </span>
                        </Flex>
                      </Flex>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {t(
                          'workspace.authentication.providersCard.description'
                        )}
                      </Text>
                    </Flex>
                    <Button
                      size="small"
                      type="link"
                      icon={<BookOpen size={14} />}
                      href={DOCS_LINKS.signInMethods}
                      target="_blank"
                      rel="noreferrer"
                      style={{ paddingInline: 0 }}
                    >
                      {t('workspace.authentication.providersCard.setupGuide')}
                    </Button>
                  </Flex>

                  <div className={styles.groupHeader}>
                    {t('workspace.authentication.providersCard.builtInGroup')}
                  </div>
                  {BUILT_IN_PROVIDERS.map((descriptor, idx) => {
                    const saved = authMethodsList.find(
                      (m) => m.method === descriptor.method
                    )

                    return (
                      <BuiltInProviderRow
                        key={descriptor.key}
                        descriptor={descriptor}
                        saved={saved}
                        isLast={idx === BUILT_IN_PROVIDERS.length - 1}
                        onSave={() => form.submit()}
                        saving={saving}
                      />
                    )
                  })}
                </Card>
              </Col>
              <Col xs={24} lg={10}>
                <AdditionalSettingsCard />
              </Col>
            </Row>

            <FormActionsBar>
              <Button onClick={handleReset} disabled={saving}>
                {t('workspace.authentication.actions.reset')}
              </Button>
              <Button
                type="primary"
                icon={<Check size={14} />}
                loading={saving}
                onClick={() => form.submit()}
              >
                {t('workspace.authentication.actions.save')}
              </Button>
            </FormActionsBar>
          </>
        )}
      </Flex>
    </Form>
  )
}
